import { useCallback, useMemo } from 'react';
import { microServicesRoutes } from '@constants';
import { contactsApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';
import type { AxiosResponse } from 'axios';

import type {
  ApiResponse,
  ContactListDto,
  CreateContactListDto,
  ContactInfoDto,
  UpdateContactDto,
} from '@features/contacts/types/contacts.types';

// Re-export types for backward compatibility
export type {
  ApiResponse,
  ContactListDto,
  CreateContactListDto,
  ContactInfoDto,
  UpdateContactDto,
} from '@features/contacts/types/contacts.types';

const TEMPLATE_COLUMNS = [
  'name',
  'phone',
  'jobTitle',
  'email',
  'accountName',
  'notes',
  'notesAgent',
  'notesSDR',
  'crmId',
] as const;

interface PaginatedContactListsResponse {
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  data: ContactListDto[];
}

function downloadBlob (blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.href = blobUrl;
  anchorElement.download = filename;
  anchorElement.click();
  URL.revokeObjectURL(blobUrl);
}

function toCsvValue (value: unknown): string {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function buildCsv (rows: Array<Record<string, unknown>>): string {
  const headerColumns = [...TEMPLATE_COLUMNS];
  const csvLines = [
    headerColumns.join(','),
    ...rows.map((row) => headerColumns.map((columnKey) => toCsvValue(row[columnKey])).join(',')),
  ];
  return csvLines.join('\n');
}

export interface UseContactsApiReturn {
  getCsvListsByOrgIdAsync: (
    organizationId: string,
    page?: string,
    pageLimit?: string,
    searchQuery?: string,
  ) => Promise<ApiResponse<ContactListDto[]>>;
  createCsvListAsync: (createData: CreateContactListDto) => Promise<ApiResponse<ContactListDto>>;
  deleteContactListAsync: (contactListId: string) => Promise<ApiResponse<null>>;
  getContactsByContactListIdAsync: (contactListId: string) => Promise<ApiResponse<ContactInfoDto[]>>;
  getContactByIdAsync: (contactId: string) => Promise<ApiResponse<ContactInfoDto>>;
  updateContactAsync: (contactUpdateData: UpdateContactDto) => Promise<ApiResponse<ContactInfoDto>>;
  updateContactFuegoLeadAsync: (contactId: string, flagged: boolean) => Promise<ApiResponse<ContactInfoDto>>;
  deleteContactAsync: (contactId: string) => Promise<ApiResponse<null>>;
  uploadCsvToListAsync: (uploadParams: {
    organizationId: string;
    contactListId: string;
    file: File;
  }) => Promise<ApiResponse<null>>;
  downloadContactsTemplateCsv: () => ApiResponse<null>;
  exportContactsOfListToCsv: (exportParams: {
    contacts: ContactInfoDto[];
    filename?: string;
  }) => ApiResponse<null>;
}

export default function useContactsApi (): UseContactsApiReturn {
  const handleError = useCallback(<T = never>(
    message: string,
    error: unknown,
    source: string,
    context?: Record<string, unknown>,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);

    void logError(message, source, {
      ...context,
      error: errorMessage,
      rawError: error,
    }, false);

    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const getCsvListsByOrgIdAsync = useCallback(async (
    organizationId: string,
    page = '1',
    pageLimit = '5',
    searchQuery = '',
  ): Promise<ApiResponse<ContactListDto[]>> => {
    const source = 'Contacts.getCsvListsByOrgIdAsync';
    try {
      const params = new URLSearchParams({ page, pageLimit });
      if (searchQuery.trim() !== '') {
        params.set('searchQuery', searchQuery);
      }

      const requestUrl = `${microServicesRoutes.contacts.get.contactList.getByOrgIdPaginated}/${organizationId}?${params.toString()}`;
      const response: AxiosResponse<PaginatedContactListsResponse> = await contactsApi.get(requestUrl);

      const contactLists = response.data?.data ?? [];

      void logInfo('Fetched contact lists.', source, {
        organizationId,
        page,
        pageLimit,
        total: response.data?.totalCount ?? contactLists.length,
      });

      return {
        success: true,
        data: contactLists,
        pageNumber: response.data?.pageNumber,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
      };
    } catch (error) {
      return handleError<ContactListDto[]>(
        'Error fetching CSV contact lists.',
        error,
        source,
        { organizationId, page, pageLimit, searchQuery },
      );
    }
  }, [handleError]);

  const createCsvListAsync = useCallback(async (
    createData: CreateContactListDto,
  ): Promise<ApiResponse<ContactListDto>> => {
    const source = 'Contacts.createCsvListAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.post.contactList.create}`;
      const response = await contactsApi.post<ContactListDto>(requestUrl, createData);

      void logInfo('Contact list created.', source, { organizationId: createData.organizationId, contactListId: response.data?.id });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactListDto>(
        'Error creating CSV contact list.',
        error,
        source,
        { organizationId: createData.organizationId, title: createData.title },
      );
    }
  }, [handleError]);

  const deleteContactListAsync = useCallback(async (
    contactListId: string,
  ): Promise<ApiResponse<null>> => {
    const source = 'Contacts.deleteContactListAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.delete.contactList.delete}/${contactListId}`;
      await contactsApi.delete(requestUrl);

      void logInfo('Contact list deleted.', source, { contactListId });

      return { success: true, data: null };
    } catch (error) {
      return handleError<null>(
        'Error deleting contact list.',
        error,
        source,
        { contactListId },
      );
    }
  }, [handleError]);

  const getContactsByContactListIdAsync = useCallback(async (
    contactListId: string,
  ): Promise<ApiResponse<ContactInfoDto[]>> => {
    const source = 'Contacts.getContactsByContactListIdAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.contact.getByContactListId}/${contactListId}`;
      const response = await contactsApi.get<ContactInfoDto[]>(requestUrl);

      const fetchedContacts = response.data ?? [];

      const warningsCount = fetchedContacts.filter((contact) => Array.isArray(contact.errorMessages) && contact.errorMessages.length > 0).length;
      void logInfo('Fetched contacts by contactListId.', source, { contactListId, total: fetchedContacts.length, warnings: warningsCount });

      return { success: true, data: fetchedContacts };
    } catch (error) {
      return handleError<ContactInfoDto[]>(
        'Error fetching contacts by contactListId.',
        error,
        source,
        { contactListId },
      );
    }
  }, [handleError]);

  const getContactByIdAsync = useCallback(async (
    contactId: string,
  ): Promise<ApiResponse<ContactInfoDto>> => {
    const source = 'Contacts.getContactByIdAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.contact.getById}/${contactId}`;
      const response = await contactsApi.get<ContactInfoDto>(requestUrl);
      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactInfoDto>(
        'Error fetching contact by id.',
        error,
        source,
        { contactId },
      );
    }
  }, [handleError]);

  const updateContactAsync = useCallback(async (
    contactUpdateData: UpdateContactDto,
  ): Promise<ApiResponse<ContactInfoDto>> => {
    const source = 'Contacts.updateContactAsync';
    try {
      const contactId = contactUpdateData.id?.trim() ?? '';
      if (contactId === '') {
        void logWarning('Contact id missing while updating.', source, { contactUpdateData });
        return { success: false, error: { message: 'Contact id is required to update.' } };
      }

      const rawPayload = {
        name: contactUpdateData.name,
        phone: contactUpdateData.phone,
        accountName: contactUpdateData.accountName,
        jobTitle: contactUpdateData.jobTitle,
        email: contactUpdateData.email,
        notes: contactUpdateData.notes,
        notesAgent: contactUpdateData.notesAgent,
        notesSDR: contactUpdateData.notesSDR,
        crmId: contactUpdateData.crmId,
        flagged: typeof contactUpdateData.flagged === 'boolean' ? contactUpdateData.flagged : false,
      };

      const filteredPayload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_key, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== ''),
      );

      filteredPayload.flagged = typeof contactUpdateData.flagged === 'boolean' ? contactUpdateData.flagged : false;

      const requestUrl = `${microServicesRoutes.contacts.put.contact.update}/${contactId}`;
      const response = await contactsApi.put<ContactInfoDto>(requestUrl, filteredPayload);

      void logInfo('Contact updated.', source, { contactId });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactInfoDto>(
        'Error trying to update contact.',
        error,
        source,
        { contactId: contactUpdateData.id },
      );
    }
  }, [handleError]);

  const updateContactFuegoLeadAsync = useCallback(async (
    contactId: string,
    flagged: boolean,
  ): Promise<ApiResponse<ContactInfoDto>> => {
    const source = 'Contacts.updateContactAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.put.contact.updateFuegoLead}/${contactId}`;
      const response = await contactsApi.put<ContactInfoDto>(requestUrl, { flagged });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactInfoDto>(
        'Error trying to update contact.',
        error,
        source,
        { contactId, flagged, },
      );
    }
  }, [handleError]);

  const deleteContactAsync = useCallback(async (
    contactId: string,
  ): Promise<ApiResponse<null>> => {
    const source = 'Contacts.deleteContactAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.delete.contact.delete}/${contactId}`;
      await contactsApi.delete(requestUrl);

      void logInfo('Contact deleted.', source, { contactId });

      return { success: true, data: null };
    } catch (error) {
      return handleError<null>(
        'Error trying to delete contact.',
        error,
        source,
        { contactId },
      );
    }
  }, [handleError]);

  const uploadCsvToListAsync = useCallback(async (
    uploadParams: {
      organizationId: string;
      contactListId: string;
      file: File;
    },
  ): Promise<ApiResponse<null>> => {
    const source = 'Contacts.uploadCsvToListAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.post.contact.uploadCsvToContactList}/${uploadParams.contactListId}`;

      const formData = new FormData();
      formData.append('contactListId', uploadParams.contactListId);
      formData.append('file', uploadParams.file);

      await contactsApi.post(requestUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { orgId: uploadParams.organizationId },
      });

      void logInfo('CSV uploaded to contact list.', source, {
        organizationId: uploadParams.organizationId,
        contactListId: uploadParams.contactListId,
        filename: uploadParams.file?.name,
        size: uploadParams.file?.size,
      });

      return { success: true, data: null };
    } catch (error) {
      return handleError<null>(
        'Error uploading CSV to list.',
        error,
        source,
        {
          organizationId: uploadParams.organizationId,
          contactListId: uploadParams.contactListId,
          filename: uploadParams.file?.name,
          size: uploadParams.file?.size,
        },
      );
    }
  }, [handleError]);

  const downloadContactsTemplateCsv = useCallback((): ApiResponse<null> => {
    const source = 'Contacts.downloadContactsTemplateCsv';
    try {
      const csvContent = `${TEMPLATE_COLUMNS.join(',')}\n`;
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(csvBlob, 'contacts-template.csv');

      void logInfo('Downloaded contacts template CSV.', source, { columns: TEMPLATE_COLUMNS });

      return { success: true, data: null };
    } catch (error) {
      return handleError<null>(
        'Error downloading contacts template.',
        error,
        source,
      );
    }
  }, [handleError]);

  const exportContactsOfListToCsv = useCallback((exportParams: {
    contacts: ContactInfoDto[];
    filename?: string;
  }): ApiResponse<null> => {
    const source = 'Contacts.exportContactsOfListToCsv';
    try {
      const filenameBase = exportParams.filename?.trim();
      const exportFilename = (filenameBase !== undefined && filenameBase !== '' ? filenameBase : 'contacts') + '.csv';

      const contactRows = (exportParams.contacts ?? []).map((contact) => ({
        name: contact.name ?? '',
        phone: contact.phone ?? '',
        jobTitle: contact.jobTitle ?? '',
        email: contact.email ?? '',
        accountName: contact.accountName ?? '',
        notes: contact.notes ?? '',
        notesAgent: contact.notesAgent ?? '',
        notesSDR: contact.notesSDR ?? '',
        crmId: contact.crmId ?? '',
      }));

      const csvContent = buildCsv(contactRows);
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(csvBlob, exportFilename);

      void logInfo('Exported contacts to CSV.', source, { filename: exportFilename, rows: contactRows.length });

      return { success: true, data: null };
    } catch (error) {
      return handleError<null>(
        'Error exporting contacts to CSV.',
        error,
        source,
        { filename: exportParams.filename, rows: exportParams.contacts?.length ?? 0 },
      );
    }
  }, [handleError]);

  return useMemo(() => ({
    getCsvListsByOrgIdAsync,
    createCsvListAsync,
    deleteContactListAsync,
    getContactsByContactListIdAsync,
    getContactByIdAsync,
    updateContactAsync,
    updateContactFuegoLeadAsync,
    deleteContactAsync,
    uploadCsvToListAsync,
    downloadContactsTemplateCsv,
    exportContactsOfListToCsv,
  }), [
    getCsvListsByOrgIdAsync,
    createCsvListAsync,
    deleteContactListAsync,
    getContactsByContactListIdAsync,
    getContactByIdAsync,
    updateContactAsync,
    updateContactFuegoLeadAsync,
    deleteContactAsync,
    uploadCsvToListAsync,
    downloadContactsTemplateCsv,
    exportContactsOfListToCsv,
  ]);
}
